import type { GovernedCapabilityIntentKind } from './governed-capability-manifest';
import type { PlannerIntentClass } from './planner-intent-class';
import { isGovernedReferenceAuthority, type GovernedReferenceAuthority } from './governed-argument-export';

// #5520 child 1 (contract freeze) — MOVED here, not duplicated, from
// apps/api/src/ask-helix/manager-runtime/manager-plan.ts. This is the shared
// contract seam: the ordered, per-requirement argument-authority carrier now
// backs every "arguments" field on the execution control plane (the manager
// plan operation, this planner call, the capability-validator's approved call,
// and the projected-question validated call/mint call), so a call built from
// one layer and passed to the next never re-shapes its arguments. Manager-
// plan.ts re-exports these under the same names for its existing importers.

/** A validated argument's value. Scalar only — never a nested object, never a
 * raw-text carrier. */
export type ManagerArgumentValue = string | number | boolean;

/**
 * How the server came to trust an argument value: the reader cited it in this
 * request, a server-minted numeric interpretation of a quoted scenario input,
 * or a manifest-trusted value the reader never had to supply. Nothing else may
 * authorize a value.
 */
export const MANAGER_ARGUMENT_CITATION_BASES = [
  'request_citation',
  'request_interpretation',
  'trusted_reference',
] as const;

export type ManagerArgumentCitationBasis =
  (typeof MANAGER_ARGUMENT_CITATION_BASES)[number];

export function isManagerArgumentCitationBasis(
  value: unknown,
): value is ManagerArgumentCitationBasis {
  return (MANAGER_ARGUMENT_CITATION_BASES as readonly unknown[]).includes(value);
}

/** Server-built nested input, bound to the scalar authority carrier by a receipt. */
export type ManagerReceiptBoundToolInput = Readonly<{
  receipt: string;
  input: Readonly<Record<string, unknown>>;
}>;

export function isManagerReceiptBoundToolInput(
  value: unknown,
): value is ManagerReceiptBoundToolInput {
  if (!isPlainJsonRecord(value)) return false;
  return (
    typeof value.receipt === 'string' &&
    value.receipt.trim().length > 0 &&
    isPlainJsonRecord(value.input) &&
    isJsonValue(value.input)
  );
}

/**
 * The VALIDATED argument authority for one exact intent+requirement slot: what
 * the validator accepted, in what governed unit, on what citation basis, with
 * what receipt, over which source refs.
 *
 * `citationReceipt` is a digest of the cited span, or for an interpretation its
 * quoted span, normalized value, and canonical unit — never the request text.
 * The reader's wording is proved, not kept: nothing here can reconstruct a
 * request, and this is the only argument authority any later turn is allowed
 * to carry. This is the minimal typed carrier: it adds no registry, service,
 * or authority beyond what already existed in the manager plan contract.
 */
export type ManagerValidatedArgumentAuthority = Readonly<{
  intentId: string;
  requirementId: string;
  value: ManagerArgumentValue;
  unit: string | null;
  citationBasis: ManagerArgumentCitationBasis;
  citationReceipt: string;
  sourceRefIds: readonly string[];
  /** Nested server-built tool input; never projected into planner arguments. */
  structuredToolInput?: ManagerReceiptBoundToolInput;
  referenceAuthority?: GovernedReferenceAuthority;
}>;

// `managerCitationReceipt` (the sha256 digest helper) moved to the Node-only
// sibling `./planner-decision-server` (#4051 P1 fix): `node:crypto` was
// reachable from the public, client-bundled `@helix/lib` barrel through this
// file, breaking the apps/web build. Import it from `@helix/lib/server`
// instead of from here.

export function isManagerArgumentValue(value: unknown): value is ManagerArgumentValue {
  if (typeof value === 'string' || typeof value === 'boolean') return true;
  return typeof value === 'number' && Number.isFinite(value);
}

/** Runtime shape guard for one authority entry — used at every boundary that
 * admits an externally-constructed carrier (the projected-question freeze,
 * the capability validator's defensive re-check). */
export function isManagerValidatedArgumentAuthority(
  value: unknown,
): value is ManagerValidatedArgumentAuthority {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.intentId === 'string' &&
    record.intentId.trim().length > 0 &&
    typeof record.requirementId === 'string' &&
    record.requirementId.trim().length > 0 &&
    isManagerArgumentValue(record.value) &&
    (record.unit === null || typeof record.unit === 'string') &&
    isManagerArgumentCitationBasis(record.citationBasis) &&
    typeof record.citationReceipt === 'string' &&
    Array.isArray(record.sourceRefIds) &&
    record.sourceRefIds.every((id) => typeof id === 'string') &&
    (record.citationBasis !== 'request_interpretation' ||
      (typeof record.value === 'number' &&
        typeof record.unit === 'string' &&
        record.unit.trim().length > 0 &&
        record.sourceRefIds.length === 0 &&
        record.referenceAuthority === undefined &&
        record.structuredToolInput === undefined)) &&
    (record.referenceAuthority === undefined ||
      (record.citationBasis === 'trusted_reference' && isGovernedReferenceAuthority(record.referenceAuthority)
        && record.value === record.referenceAuthority.export.referenceId)) &&
    (record.structuredToolInput === undefined ||
      isManagerReceiptBoundToolInput(record.structuredToolInput))
  );
}

/**
 * #5520 terra P0-A HOLD (ruling on ad48b1f1e0d1) — a PLANNED call and a
 * VALIDATED call carry DIFFERENT types. This is what the PLANNER claims: a
 * requirement id, a value, and (optionally) the basis on which the planner
 * says the value is trusted. It carries no authority: a server-built manager
 * operation may add an opaque structured-input receipt pointer, but the
 * structure remains solely in the validated carrier. `capability-validator.ts`
 * is the ONLY place that turns one of these into a
 * `ManagerValidatedArgumentAuthority`, and only after verifying the claim
 * against reader text, the trusted-reference set, or a manifest default. A
 * claim this module cannot verify is a typed validation failure, never a
 * silently downgraded authority.
 */
export type ManagerPlannedArgument = Readonly<{
  requirementId: string;
  value: ManagerArgumentValue;
  /** Server-only pointer to nested input retained in the validated carrier. */
  structuredToolInputReceipt?: string;
  citationClaim?: Readonly<{
    basis: ManagerArgumentCitationBasis;
    /** Verbatim reader span. Required when basis is request_citation. */
    span?: string;
    /** Unit spelling carried inside span; null means the requirement is unitless. */
    unit?: string | null;
    sourceRefIds?: readonly string[];
  }>;
}>;

/** Runtime shape guard for one planned argument entry — the defensive
 * re-check at every boundary that admits an externally-constructed planned
 * call (provider JSON, a resumed run, a wiring defect). */
export function isManagerPlannedArgument(value: unknown): value is ManagerPlannedArgument {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  if (
    typeof record.requirementId !== 'string' ||
    record.requirementId.trim().length === 0 ||
    !isManagerArgumentValue(record.value)
  ) {
    return false;
  }
  if (
    record.structuredToolInputReceipt !== undefined &&
    (typeof record.structuredToolInputReceipt !== 'string' ||
      record.structuredToolInputReceipt.trim().length === 0)
  ) {
    return false;
  }
  if (record.citationClaim === undefined) return true;
  if (typeof record.citationClaim !== 'object' || record.citationClaim === null) return false;
  const claim = record.citationClaim as Record<string, unknown>;
  return (
    isManagerArgumentCitationBasis(claim.basis) &&
    (claim.span === undefined || typeof claim.span === 'string') &&
    (claim.unit === undefined || claim.unit === null || typeof claim.unit === 'string') &&
    (claim.sourceRefIds === undefined ||
      (Array.isArray(claim.sourceRefIds) &&
        claim.sourceRefIds.every((id) => typeof id === 'string')))
  );
}

/** Projects the scalar planner view and retains only a receipt pointer to nested input. */
export function managerPlannedArgumentsFromValidatedAuthorities(
  authorities: readonly ManagerValidatedArgumentAuthority[],
): readonly ManagerPlannedArgument[] {
  return authorities.map((authority) => ({
    requirementId: authority.requirementId,
    value: authority.value,
    ...(authority.structuredToolInput
      ? { structuredToolInputReceipt: authority.structuredToolInput.receipt }
      : {}),
  }));
}

/** Rehydrated authority is usable only when its scalar projection and receipt pointers agree. */
export function managerPlannedArgumentsMatchValidatedAuthorities(
  planned: readonly ManagerPlannedArgument[],
  authorities: readonly ManagerValidatedArgumentAuthority[],
): boolean {
  const expected = managerPlannedArgumentsFromValidatedAuthorities(authorities);
  return planned.length === expected.length && planned.every((argument, index) =>
    argument.requirementId === expected[index]?.requirementId &&
    argument.value === expected[index]?.value &&
    argument.structuredToolInputReceipt === expected[index]?.structuredToolInputReceipt,
  );
}

/**
 * Server-minted scenario values that are structured by contract and therefore
 * cannot travel in the scalar argument-authority carrier.
 */
export type ManagerTrustedScenarioInput = Readonly<{
  optionFamilies?: readonly ('speed' | 'structural' | 'strategy')[];
  constraints?: Readonly<Record<string, unknown>>;
  trustedConstraintAck?: Readonly<{ disableExecutiveProtection: true }>;
}>;

const MANAGER_TRUSTED_SCENARIO_INPUT_KEYS = new Set([
  'optionFamilies',
  'constraints',
  'trustedConstraintAck',
]);

export function isManagerTrustedScenarioInput(
  value: unknown,
): value is ManagerTrustedScenarioInput {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (
    Object.keys(record).some(
      (key) => !MANAGER_TRUSTED_SCENARIO_INPUT_KEYS.has(key),
    )
  ) {
    return false;
  }
  if (
    record.optionFamilies !== undefined &&
    (!Array.isArray(record.optionFamilies) ||
      !record.optionFamilies.every(
        (family) =>
          family === 'speed' || family === 'structural' || family === 'strategy',
      ))
  ) {
    return false;
  }
  if (
    record.constraints !== undefined &&
    (typeof record.constraints !== 'object' ||
      record.constraints === null ||
      Array.isArray(record.constraints))
  ) {
    return false;
  }
  if (record.trustedConstraintAck === undefined) return true;
  const ack = record.trustedConstraintAck;
  return (
    typeof ack === 'object' &&
    ack !== null &&
    !Array.isArray(ack) &&
    Object.keys(ack as Record<string, unknown>).length === 1 &&
    (ack as Record<string, unknown>).disableExecutiveProtection === true
  );
}

/** The first authority for a requirement id, or `undefined` when the slot was
 * never filled. Shared lookup so consumers never re-implement a linear scan
 * over the ordered carrier. */
export function managerArgumentValue(
  authorities: readonly ManagerValidatedArgumentAuthority[],
  requirementId: string,
): ManagerArgumentValue | undefined {
  return authorities.find((authority) => authority.requirementId === requirementId)?.value;
}

/** The first planner claim for a requirement id, before the validator mints authority. */
export function managerPlannedArgumentValue(
  planned: readonly ManagerPlannedArgument[],
  requirementId: string,
): ManagerArgumentValue | undefined {
  return planned.find((argument) => argument.requirementId === requirementId)?.value;
}

function isPlainJsonRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
  );
}

function isJsonValue(value: unknown): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(isJsonValue);
  return isPlainJsonRecord(value) && Object.values(value).every(isJsonValue);
}

/**
 * A flat `requirementId -> value` projection of the ordered carrier, for
 * DISPLAY/TRACE consumers only (narration, run-ledger traces, debug output)
 * — never re-admitted as validated execution arguments. The frozen carrier
 * stays the ordered array everywhere on the control plane; this is a
 * read-only view for the one class of consumer that legitimately wants a
 * keyed record and never feeds it back into execution or validation.
 */
export function managerArgumentAuthorityRecordView(
  authorities: readonly ManagerValidatedArgumentAuthority[],
): Readonly<Record<string, ManagerArgumentValue>> {
  return Object.fromEntries(
    authorities.map((authority) => [authority.requirementId, authority.value]),
  );
}

export type PlannerDecisionTurnType =
  | 'context_readback'
  | 'single_capability'
  | 'multi_capability'
  | 'scenario_plan'
  | 'clarification'
  | 'blocked';

// Structural projection of the planner's industry-agnostic `intentClass` onto the
// existing turnType space. Used as a CROSS-CHECK against the structurally-derived
// turnType, never as the sole source of truth. `unresolved` returns null so the
// caller keeps the structurally-derived turnType (defense in depth).
export function projectTurnTypeFromIntentClass(
  intentClass: PlannerIntentClass | null | undefined,
): PlannerDecisionTurnType | null {
  switch (intentClass) {
    case 'provenance_drilldown':
    case 'evidence_status':
    case 'metric_readout':
      return 'single_capability';
    case 'value_gap_bridge':
      return 'multi_capability';
    case 'value_gap_contribution':
      return 'single_capability';
    case 'decision_package':
      return 'multi_capability';
    case 'scenario_plan':
      return 'scenario_plan';
    case 'context_readback':
      return 'context_readback';
    case 'out_of_scope':
      return 'blocked';
    case 'unresolved':
    case null:
    case undefined:
    default:
      return null;
  }
}

export type PlannedCapabilityCall = {
  callId: string;
  capabilityId: string;
  toolName: string;
  intentId: string;
  intentKind: GovernedCapabilityIntentKind;
  /**
   * The planner's ordered per-requirement claims. They carry neither a
   * receipt nor execution authority; the capability validator mints the
   * validated carrier after it verifies each claim.
   */
  arguments: readonly ManagerPlannedArgument[];
  /** Server-minted structured scenario input; provider plans never populate it. */
  trustedScenarioInput?: ManagerTrustedScenarioInput;
  required: boolean;
};

/**
 * Provider-suggested industry practice for narrator awareness only. This is
 * deliberately not a capability declaration: the closed registry and the
 * CapabilityValidator remain the only paths that can admit executable work.
 */
export type IndustryPracticeSuggestion = {
  /** Server-assigned deterministic response-local identity. */
  suggestionId: string;
  label: string;
  rationale: string;
};

export type TransformationPlan = {
  objective: string;
  targetMetric?: string | null;
  targetDelta?: number | null;
  constraints: string[];
  capabilityIds: string[];
};

// GOVERNED-CONFIG AUTHORITY (#4004 P2, V1 — closure relocated, never removed):
// the objective vocabulary now lives in governed, checksummed config at
// apps/api/config/ask-helix/objective-taxonomy/objective-vocabulary.json
// (contract: ObjectiveVocabularyFile in packages/lib/src/objective-taxonomy-resolver.ts).
// This enum REMAINS as the compile-time type so existing call sites keep their
// closed-union typing (backward compat), but it is a PROJECTION of the config,
// not the source of truth. Invariant, spec-enforced in
// apps/api/src/ask-helix/objective-taxonomy/objective-vocabulary.spec.ts:
//   enum ⊆ config (bijection today).
// N+1 PROCESS (pinned): a new objective is added to the governed config FIRST
// (entry + checksum re-approval). A config objectiveId missing from this enum
// is the N+1 signal for the next type regen — extend this literal list (and
// OUTCOME_TARGET_METRIC_IDS below) in the same change; the spec fails closed
// until the two agree. Never add an objective here that the config does not
// carry.
export const OUTCOME_PLAN_OBJECTIVES = [
  'improve_ebitda',
  'improve_operating_income',
  'improve_margin',
  'reduce_cost',
  'increase_revenue',
  'improve_delivery_time',
  'improve_customer_experience',
] as const;

export type OutcomePlanObjective = (typeof OUTCOME_PLAN_OBJECTIVES)[number];

// Pure objective → target-metric-id mapping. Lives in the shared contract (not the
// app-side normalizer) so it is reusable and so the planner normalizer carries no
// domain-metric literals. This is a lookup table, NOT question-keyword routing.
// RELOCATED (#4004 P2): the governed copy of this content lives in
// objective-vocabulary.json (see the authority note above). This table is the
// compile-time mirror kept for existing callers; the vocabulary spec pins each
// entry equal to the config, so an edit on either side without the other fails
// closed. Prefer resolveObjectiveTargetMetric() from the config-backed
// ObjectiveVocabularyResolver in new code.
const OUTCOME_TARGET_METRIC_IDS: Readonly<Record<OutcomePlanObjective, string>> = {
  improve_ebitda: 'metric:ebitda:v1',
  improve_operating_income: 'metric:operating_income:v1',
  improve_margin: 'margin',
  reduce_cost: 'cost',
  increase_revenue: 'revenue',
  improve_delivery_time: 'delivery_time',
  improve_customer_experience: 'customer_experience',
};

export function outcomeTargetMetricId(objective: OutcomePlanObjective): string | null {
  return OUTCOME_TARGET_METRIC_IDS[objective] ?? null;
}

// The outcome objectives that close an enterprise value gap across multiple levers
// (the composite finance bridge). Owned here in the shared contract — NOT in the
// app-side spine — so the planner normalizer can ask `isFinanceValueGapObjective`
// without carrying domain-metric literals. This is a typed enum membership check,
// NOT question-keyword routing.
export const FINANCE_VALUE_GAP_OBJECTIVES = [
  'improve_ebitda',
  'improve_operating_income',
] as const satisfies readonly OutcomePlanObjective[];

export function isFinanceValueGapObjective(
  objective: OutcomePlanObjective,
): boolean {
  return (FINANCE_VALUE_GAP_OBJECTIVES as readonly OutcomePlanObjective[]).includes(
    objective,
  );
}

export const OUTCOME_PLAN_TARGET_DELTA_UNITS = [
  'percent',
  'currency',
  'bps',
  'days',
] as const;

export type OutcomePlanTargetDeltaUnit =
  (typeof OUTCOME_PLAN_TARGET_DELTA_UNITS)[number];

export type OutcomePlanTargetDelta = {
  value: number;
  unit: OutcomePlanTargetDeltaUnit;
};

export const SCENARIO_PLAN_CASE_KINDS = [
  'base',
  'upside',
  'downside',
  'stress',
  'management_case',
] as const;

export type ScenarioPlanCaseKind =
  (typeof SCENARIO_PLAN_CASE_KINDS)[number];

export type ScenarioRange = {
  scenarioId: string;
  label: string;
  caseKind: ScenarioPlanCaseKind;
  targetDelta?: OutcomePlanTargetDelta | null;
  targetDeltaRange?: {
    low: OutcomePlanTargetDelta;
    high: OutcomePlanTargetDelta;
  } | null;
  assumptions: string[];
  requiredCapabilityIds: string[];
  optionalCapabilityIds: string[];
  evidenceRefIds: string[];
};

export type ScenarioSet = {
  scenarioSetId: string;
  comparisonPolicy: 'mutually_exclusive_cases' | 'portfolio_options';
  scenarios: ScenarioRange[];
  selectedScenarioId?: string | null;
};

export type ScenarioPlan = {
  scenarioPlanId: string;
  objective: OutcomePlanObjective;
  targetMetric?: string | null;
  scenarioSet: ScenarioSet;
  constraints: string[];
  strategyGroundingRequired: boolean;
};

export type OutcomePlan = {
  outcomePlanId: string;
  objective: OutcomePlanObjective;
  targetMetric?: string | null;
  targetDelta?: OutcomePlanTargetDelta | null;
  requiredCapabilityIds: string[];
  optionalCapabilityIds: string[];
  excludedCapabilityIds: string[];
  leverTaxonomyRefs: string[];
  constraints: string[];
  missingRequiredBehavior: 'block';
  missingOptionalBehavior: 'name_gap_and_continue';
  strategyGroundingRequired: boolean;
};

export type PlannerDecision = {
  decisionId: string;
  turnType: PlannerDecisionTurnType;
  // Industry-agnostic question archetype emitted by the planner LLM. Runtime
  // planning frames must carry a valid enum value; use `unresolved` explicitly
  // when the planner cannot classify a turn. This is the canonical signal the
  // answer-path routers should prefer over raw-question keyword matching.
  intentClass?: PlannerIntentClass | null;
  userIntent: string;
  contextRevisionId: string;
  capabilityCatalogRevisionId: string;
  proposedCalls: PlannedCapabilityCall[];
  /** Advisory-only discovery rows. They never grant execution authority. */
  industryPracticeSuggestions?: IndustryPracticeSuggestion[];
  transformationPlan?: TransformationPlan | null;
  outcomePlan?: OutcomePlan | null;
  scenarioPlan?: ScenarioPlan | null;
  blockedReason?: string | null;
};

export type PendingValidatorEnvelope = Readonly<{
  status: 'blocked_pending_validator';
  plannerDecisionId: string;
  requestedCapabilityIds: readonly string[];
  reason: 'validator_not_available' | 'execution_gateway_not_enabled';
  unblockPr: 'PR4';
}>;
